/**
 * Best-effort readable representation of any rejection value — several layers in the app
 * reject with strings, plain feathers objects or even null/undefined instead of `Error`s.
 */
export function errorMessageOf(error: unknown): string {
  if (!error) {
    return String(error);
  }
  if (typeof error === 'string') {
    return error;
  }
  const candidate = error as { message?: unknown };
  if (typeof candidate.message === 'string' && candidate.message.length > 0) {
    return candidate.message;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return Object.prototype.toString.call(error);
  }
}

/**
 * Sentry needs a real `Error` to attach a stack trace and to show any payload at all —
 * a `null`/`undefined` rejection reaches Sentry as literally `null` and carries zero
 * diagnostic value (work item 27022). Wraps non-Error rejection values into an `Error`
 * that preserves the original content in its message.
 */
export function toError(error: unknown, context: string): Error {
  if (error instanceof Error) {
    return error;
  }
  const wrapped = new Error(`${context} — rejected with non-Error value: ${errorMessageOf(error)}`);
  wrapped.name = 'NonErrorRejection';
  return wrapped;
}
